import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { AnnouncementBar } from "./AnnouncementBar";
import { SOCIALS } from "./SocialLinks";
import { COACH_URL } from "./Coach";
import { useCart } from "@/context/CartContext";
import { formationHref } from "./FormationBox";
import { useLang, shopPath, counterpart } from "@/lib/i18n";

const MENUS = {
  soins: {
    label: "Soins",
    items: [
      { to: "/shampoings", label: "Shampoings", hint: "Laver & fortifier", img: "/products/real/u12-shamp-cactus.webp" },
      { to: "/masques", label: "Masques", hint: "Le soin profond", img: "/products/real/u23-masque-supreme.webp" },
      { to: "/huiles-serums", label: "Huiles & sérums", hint: "Les concentrés", img: "/products/real/u01-extreme.webp" },
      { to: "/cremes-coiffantes", label: "Crèmes coiffantes", hint: "Coiffer & sublimer", img: "/products/real/u03-cactus-chute.webp" },
      { to: "/gels", label: "Gels", hint: "Définir & fixer", img: "/products/real/u06-gel-boucles.webp" },
    ],
    extras: [
      { to: "/produit/serum-anti-taches-lumina-vg", label: "Nouveauté : Sérum Lumina V.G" },
      { to: "/boutique?type=unite", label: "Tous les soins à l'unité" },
      { to: "/locks", label: "Locks" },
      { to: "/barbe", label: "Barbe" },
      { to: "/boutique?category=Soins%20visage", label: "Soins visage" },
      { to: "/boutique?category=Soins%20du%20corps", label: "Soins du corps" },
      { to: "/composer", label: "Composer mon pack" },
      { to: "/abonnement?formule=duo", label: "Abonnement Duo : 2 soins 19,99 €/mois" },
      { to: "/abonnement?formule=famille", label: "Abonnement Famille : 4 soins 39,90 €/mois" },
    ],
  },
  guides: {
    label: "Guides",
    items: [
      { to: "/cheveux-afro-crepus", label: "Cheveux afro & crépus", hint: "Le guide complet" },
      { to: "/diagnostic", label: "Diagnostic gratuit", hint: "Votre routine en 1 minute" },
      { to: "/diagnostic-cheveux-afro", label: "Guide diagnostic cheveux", hint: "Faire le point en 5 questions" },
      { to: "/chute-de-cheveux-alopecie", label: "Chute & alopécie", hint: "Comprendre et agir" },
      { to: "/pousse-des-cheveux", label: "Pousse des cheveux", hint: "Garder la longueur" },
    ],
    extras: [
      { to: "/conseils", label: "Tous nos conseils" },
      { to: "/alopecie-de-traction", label: "Alopécie de traction" },
      { to: "/repousse-tempes-contours", label: "Tempes et contours" },
      { to: "/cheveux-en-bonne-sante", label: "Cheveux en bonne santé" },
      { to: "/choisir-shampoing-cheveux-afro", label: "Choisir son shampoing" },
      { to: "/faq-cheveux-afro", label: "FAQ cheveux afro" },
      { to: "/glossaire-cheveux-afro", label: "Glossaire cheveux afro" },
    ],
  },
};

/** Menus en anglais : uniquement des pages disponibles en anglais (boutique filtrée et guides /en). */
const MENUS_EN = {
  soins: {
    label: "Soins",
    items: [
      { to: "/en/shop?category=Pousse", label: "Growth range", hint: "Hair loss & length", img: "/products/real/u01-extreme.webp" },
      { to: "/en/shop?type=unite", label: "Single products", hint: "Shampoos, masks, oils, gels", img: "/products/real/u12-shamp-cactus.webp" },
      { to: "/en/shop?type=pack", label: "Ozentya packs", hint: "Complete routines", img: "/products/real/u23-masque-supreme.webp" },
      { to: "/en/shop?category=Locks", label: "Locs", hint: "Cleanse & nourish", img: "/products/real/u03-cactus-chute.webp" },
      { to: "/en/shop?category=Barbe", label: "Beard", hint: "Care & shine", img: "/products/real/u06-gel-boucles.webp" },
    ],
    extras: [
      { to: "/en/product/serum-anti-taches-lumina-vg", label: "New: Lumina V.G serum" },
      { to: "/en/shop", label: "All products" },
      { to: "/en/shop?category=Soins%20visage", label: "Face care" },
      { to: "/en/shop?category=Soins%20du%20corps", label: "Body care" },
      { to: "/en/shop?category=Kids", label: "Kids" },
    ],
  },
  guides: {
    label: "Guides",
    items: [
      { to: "/en/hair-loss-afro-hair", label: "Hair loss & afro hair", hint: "Understand and act" },
      { to: "/en/traction-alopecia", label: "Traction alopecia", hint: "Spot it, protect your roots" },
      { to: "/en/edges-temples-care", label: "Edges & temples", hint: "A gentle hairline routine" },
      { to: "/en/afro-hair-growth", label: "Afro hair growth", hint: "Keep your length" },
      { to: "/en/kinky-coily-hair-care", label: "Kinky & coily hair", hint: "Your natural routine" },
    ],
    extras: [
      { to: "/en/hair-diagnosis", label: "Hair diagnosis" },
      { to: "/en/locs-dreadlocks-care", label: "Locs & dreadlocks" },
      { to: "/en/beard-care", label: "Beard care" },
      { to: "/en/faq", label: "FAQ" },
      { to: "/en/afro-hair-glossary", label: "Afro hair glossary" },
      { to: "/en/international-shipping", label: "International shipping" },
      { to: "/en/wholesale-resellers", label: "Wholesale & resellers" },
    ],
  },
};
const menusFor = (lang) => (lang === "en" ? MENUS_EN : MENUS);

/** Drapeaux en SVG (les emojis drapeaux ne s'affichent pas sous Windows). */
const FlagFR = () => (
  <svg viewBox="0 0 3 2" className="h-3.5 w-5 rounded-[2px] shadow-[0_0_0_1px_rgba(255,255,255,0.25)]" aria-hidden="true">
    <rect width="1" height="2" fill="#0055A4" /><rect x="1" width="1" height="2" fill="#FFFFFF" /><rect x="2" width="1" height="2" fill="#EF4135" />
  </svg>
);
const FlagGB = () => (
  <svg viewBox="0 0 60 30" className="h-3.5 w-5 rounded-[2px] shadow-[0_0_0_1px_rgba(255,255,255,0.25)]" aria-hidden="true">
    <clipPath id="gb-c"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
    <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
    <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#gb-c)" stroke="#C8102E" strokeWidth="4" />
    <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
    <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);
const FLAGS = { fr: { Flag: FlagFR, name: "Français" }, en: { Flag: FlagGB, name: "English" } };

/** En anglais, la boutique pointe vers /en/shop (sinon le site repasserait en français). */
const loc = (to, lang) => (lang === "en" ? to.replace(/^\/boutique/, "/en/shop") : to);

const linkCls = "group relative whitespace-nowrap text-sm tracking-wide text-[var(--parchment)]/85 hover:text-[var(--gold-soft)]";
const underline = <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--gold)] transition-[width] duration-500 group-hover:w-full" />;

function Thumb({ src, label, size }) {
  const { t } = useLang();
  return (
    <span className={`${size} shrink-0 overflow-hidden rounded-sm border border-[var(--line)] bg-[var(--ink-2)]`}>
      <img src={src} alt={`${t("Rubrique")} ${label} Ozentya`} width="56" height="56" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover/item:scale-110" />
    </span>
  );
}

function DropMenu({ id }) {
  const { t, lang } = useLang();
  const m = menusFor(lang)[id];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  const active = m.items.some((s) => s.to === pathname);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" data-testid={`nav-${id}-button`} aria-expanded={open} aria-haspopup="true" onClick={() => setOpen((o) => !o)}
        className={`${linkCls} flex items-center gap-1 ${active ? "text-[var(--gold-soft)]" : ""}`}>
        {t(m.label)} <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.2 }}
            className="absolute left-1/2 top-full z-50 w-80 -translate-x-1/2 pt-4">
            <div data-testid={`nav-${id}-menu`} className="overflow-hidden rounded-sm border border-[var(--line)] bg-[rgba(11,8,9,0.96)] py-2 shadow-2xl backdrop-blur-xl">
              <ul>
                {m.items.map((s) => (
                  <li key={s.to}>
                    <Link to={loc(s.to, lang)} data-testid={`nav-link-${s.label}`} onClick={() => setOpen(false)}
                      className={`group/item flex items-center gap-4 px-4 py-3 transition-colors duration-300 hover:bg-[var(--ink-2)] ${pathname === s.to ? "text-[var(--gold-soft)]" : ""}`}>
                      {s.img ? <Thumb src={s.img} label={s.label} size="h-14 w-14" /> : null}
                      <span>
                        <span className="block font-display text-xl italic">{t(s.label)}</span>
                        <span className="block text-xs uppercase tracking-[0.18em] text-[var(--parchment)]/70">{t(s.hint)}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <ul className="mx-4 mt-1 flex flex-wrap gap-x-4 gap-y-2 border-t border-[var(--line)] py-3">
                {m.extras.map((s) => (
                  <li key={s.to}><Link to={loc(s.to, lang)} data-testid={`nav-link-${s.label}`} onClick={() => setOpen(false)} className="text-sm text-[var(--parchment)]/85 hover:text-[var(--gold-soft)]">{t(s.label)}</Link></li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileGroup({ id, close }) {
  const { t, lang } = useLang();
  const m = menusFor(lang)[id];
  return (
    <div data-testid={`mobile-nav-${id}`} className="border-b border-[var(--line)] py-4">
      <p className="font-display text-2xl italic">{t(m.label)}</p>
      <div className="mt-2 flex flex-col pl-4">
        {m.items.map((s) => (
          <Link key={s.to} to={loc(s.to, lang)} onClick={close} data-testid={`mobile-nav-link-${s.label}`} className="flex items-center gap-4 py-2 text-base text-[var(--parchment)]/85 hover:text-[var(--gold-soft)]">
            {s.img ? <Thumb src={s.img} label={s.label} size="h-12 w-12" /> : null}
            <span>
              <span className="block">{t(s.label)}</span>
              <span className="block text-xs uppercase tracking-[0.18em] text-[var(--parchment)]/60">{t(s.hint)}</span>
            </span>
          </Link>
        ))}
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
          {m.extras.map((s) => (
            <Link key={s.to} to={loc(s.to, lang)} onClick={close} data-testid={`mobile-nav-link-${s.label}`} className="py-1 text-sm text-[var(--gold-soft)]">{t(s.label)}</Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function LangSwitch() {
  const { lang, setLang } = useLang();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const go = (to) => { setLang(to); const p = counterpart(pathname, search, to); if (p) navigate(p); };
  return (
    <div data-testid="lang-switch" className="flex h-10 items-center rounded-full border border-[var(--line)] p-0.5 text-[11px] font-semibold sm:h-11 sm:p-1">
      {["fr", "en"].map((l) => {
        const { Flag, name } = FLAGS[l];
        return (
          <button key={l} type="button" onClick={() => go(l)} data-testid={`lang-${l}`} aria-pressed={lang === l} lang={l} aria-label={name} title={name}
            className={`flex h-9 items-center gap-1.5 rounded-full px-2 uppercase transition-colors sm:px-2.5 ${lang === l ? "bg-[var(--gold)] text-[var(--ink)]" : "text-[var(--parchment)]/85 hover:text-[var(--gold-soft)]"}`}>
            <Flag /><span className="hidden sm:inline">{l}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Navbar() {
  const { count, setOpen } = useCart();
  const { lang, t } = useLang();
  const en = lang === "en";
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();
  const solid = scrolled || /^\/(ambassadeurs|espace-ambassadeur|revendeurs|diagnostic)/.test(pathname); // light-background pages: keep the bar readable at the top
  const close = () => setMenu(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        solid ? "border-b border-[var(--line)] bg-[rgba(11,8,9,0.92)] backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <AnnouncementBar />
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between gap-2 px-4 sm:px-5 md:px-10 min-[1720px]:max-w-[1800px]">
        <Link to={lang === "en" ? "/en" : "/"} data-testid="nav-logo-link" className="shrink-0"><Logo /></Link>
        <nav className="hidden items-center gap-3 min-[1440px]:flex min-[1720px]:gap-6">
          <NavLink to={`${shopPath(lang)}?type=pack`} data-testid="nav-link-Coffrets" className={linkCls}>{t("Packs Ozentya")}{underline}</NavLink>
          <NavLink to={`${shopPath(lang)}?type=unite`} data-testid="nav-link-unite" className={linkCls}>{t("Soins à l'unité")}{underline}</NavLink>
          {en ? null : <NavLink to="/abonnement" data-testid="nav-link-subscription" className={`${linkCls} !text-[var(--gold-soft)]`}>{t("Abonnement")}{underline}</NavLink>}
          <DropMenu id="soins" />
          <DropMenu id="guides" />
          {en ? null : <NavLink to="/a-propos" data-testid="nav-link-about" className={`${linkCls} hidden min-[1720px]:inline-block`}>{t("À propos")}{underline}</NavLink>}
          {en ? null : <NavLink to="/ambassadeurs" data-testid="nav-link-ambassadors" className={linkCls}>{t("Ambassadeurs")}{underline}</NavLink>}
          <NavLink to={lang === "en" ? "/en/wholesale-resellers" : "/revendeurs"} data-testid="nav-link-resellers" className={linkCls}>{t("Revendeurs")}{underline}</NavLink>
          <a href={formationHref("menu")} target="_blank" rel="noopener" data-testid="nav-link-formation" className={`${linkCls} !text-[var(--gold-soft)]`}>{t("Formation")}{underline}</a>
          {en ? null : <a href={COACH_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-link-coach" className={`${linkCls} hidden min-[1720px]:inline-block`}>{t("Mon Coach Cheveux")}{underline}</a>}
          <a href={SOCIALS.pro} target="_blank" rel="noopener noreferrer" data-testid="nav-link-pro" className="whitespace-nowrap rounded-full border border-[var(--gold)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--gold-soft)] transition-colors duration-300 hover:bg-[var(--gold)] hover:text-[var(--ink)]">{t("Pack Pro")}<span className="hidden min-[1720px]:inline">{t(" · Achat-revente")}</span></a>
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <LangSwitch />
          <button data-testid="nav-cart-button" onClick={() => setOpen(true)} className="relative flex h-10 items-center gap-2 rounded-full border border-[var(--line)] px-3 text-sm transition-colors hover:border-[var(--gold)] hover:text-[var(--gold-soft)] sm:h-11 sm:px-4">
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">{t("Panier")}</span>
            <span data-testid="nav-cart-count" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--gold)] px-1.5 text-[11px] font-semibold text-[var(--ink)]">{count}</span>
          </button>
          <button data-testid="nav-mobile-menu-button" onClick={() => setMenu((m) => !m)} className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] sm:h-11 sm:w-11 min-[1440px]:hidden" aria-label="Menu">
            {menu ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.nav initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="max-h-[calc(100dvh-116px)] overflow-y-auto border-t border-[var(--line)] bg-[var(--ink)] px-6 py-6 min-[1440px]:hidden">
            <Link to={`${shopPath(lang)}?type=pack`} onClick={close} data-testid="mobile-nav-link-Coffrets" className="block border-b border-[var(--line)] py-4 font-display text-2xl italic">{t("Packs Ozentya")}</Link>
            <Link to={`${shopPath(lang)}?type=unite`} onClick={close} data-testid="mobile-nav-link-unite" className="block border-b border-[var(--line)] py-4 font-display text-2xl italic">{t("Soins à l'unité")}</Link>
            {en ? null : <Link to="/abonnement" onClick={close} data-testid="mobile-nav-link-subscription" className="block border-b border-[var(--line)] py-4 font-display text-2xl italic text-[var(--gold-soft)]">{t("Abonnement Duo · 2 soins 19,99 €/mois")}</Link>}
            <MobileGroup id="soins" close={close} />
            <MobileGroup id="guides" close={close} />
            {en ? null : <>
            <Link to="/a-propos" onClick={close} data-testid="mobile-nav-link-about" className="block border-b border-[var(--line)] py-4 font-display text-2xl italic">{t("À propos")}</Link>
            <Link to="/ambassadeurs" onClick={close} data-testid="mobile-nav-link-ambassadors" className="block border-b border-[var(--line)] py-4 font-display text-2xl italic">{t("Ambassadeurs")}</Link>
            <Link to="/diagnostic" onClick={close} data-testid="mobile-nav-link-diagnostic" className="block border-b border-[var(--line)] py-4 font-display text-2xl italic">{t("Diagnostic gratuit")}</Link>
            </>}
            <Link to={lang === "en" ? "/en/wholesale-resellers" : "/revendeurs"} onClick={close} data-testid="mobile-nav-link-resellers" className="block border-b border-[var(--line)] py-4 font-display text-2xl italic">{t("Revendeurs")}</Link>
            <a href={formationHref("menu")} target="_blank" rel="noopener" data-testid="mobile-nav-link-formation" className="block border-b border-[var(--line)] py-4 font-display text-2xl italic">{t("Formation")}</a>
            <a href={COACH_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-link-coach" className="block border-b border-[var(--line)] py-4 font-display text-2xl italic">{t("Mon Coach Cheveux")}</a>
            <a href={SOCIALS.pro} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-link-pro" className="mt-5 flex h-12 items-center justify-center rounded-full bg-[var(--gold)] text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)]">{t("Pack Pro · Achat-revente")}</a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
